import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const BASE_URL = "https://anugraha.co";

/**
 * SEOHead — Reusable per-page SEO metadata component.
 *
 * @param {string}  title       – Page-specific <title> tag
 * @param {string}  description – Page-specific meta description
 * @param {string}  path        – Canonical URL path (e.g. "/product/raptor")
 * @param {string}  [ogImage]   – Optional Open Graph image path
 * @param {string}  [ogType]    – Optional Open Graph type (default: "website")
 */
const SEOHead = ({ title, description, path = "/", ogImage, ogType = "website" }) => {
  const canonicalUrl = `${BASE_URL}${path}`;
  const ogImageUrl = ogImage ? `${BASE_URL}${ogImage}` : `${BASE_URL}/AEPL_lOGO.svg`;

  useEffect(() => {
    if (title) {
      document.title = title;
      const titleTag = document.querySelector("title");
      if (titleTag) {
        titleTag.innerText = title;
      }
      setMetaTag("og:title", title, "property");
      setMetaTag("twitter:title", title, "name");
    }
    if (description) {
      setMetaTag("description", description, "name");
      setMetaTag("og:description", description, "property");
      setMetaTag("twitter:description", description, "name");
    }
    setMetaTag("og:url", canonicalUrl, "property");
    setMetaTag("og:type", ogType, "property");
    setMetaTag("og:image", ogImageUrl, "property");
    setMetaTag("twitter:image", ogImageUrl, "name");

    let canonicalLink = document.querySelector("link[rel='canonical']");
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonicalUrl);
  }, [title, description, path, ogImage, ogType, canonicalUrl, ogImageUrl]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content="AEPL, Anugraha Exceed, Aviation Training, Defence Training, Energy Training, AI Training Intelligence, Raptor TMS, Griffin XR, Aurora Pilot Training" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:site_name" content="Anugraha Exceed Pvt Ltd" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Anugraha Exceed Private Limited",
          "alternateName": ["AEPL", "Anugraha Exceed Pvt Ltd", "Anugraha Exceed", "Anugraha"],
          "url": "https://anugraha.co/",
          "logo": "https://anugraha.co/AEPL_lOGO.svg",
          "image": "https://anugraha.co/AEPL_lOGO.svg",
          "description": "Anugraha Exceed Private Limited (AEPL) builds AI/ML & XR training intelligence platforms for Aviation, Defence & Energy industries.",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Garuda BHIVE Workspace, BTM Layout",
            "addressLocality": "Bengaluru",
            "addressRegion": "Karnataka",
            "postalCode": "560076",
            "addressCountry": "IN"
          },
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "telephone": "+91 636 184 2502",
              "email": "sales@anugraha.co",
              "contactType": "sales",
              "availableLanguage": ["English", "Hindi"]
            }
          ],
          "founder": [
            {
              "@type": "Person",
              "name": "Nalini Nadupalli"
            },
            {
              "@type": "Person",
              "name": "Sridhar Krishna"
            }
          ],
          "sameAs": [
            "https://x.com/Anugraha_Exceed",
            "https://www.linkedin.com/company/anugraha-exceed-incorporated/",
            "https://www.instagram.com/anugraha_exceed_pvt_ltd/",
            "https://www.facebook.com/profile.php?id=61568297756356"
          ],
          "knowsAbout": [
            "Aviation Training Management Software",
            "Defence Force Readiness Platforms",
            "Energy Sector Safety Training",
            "AI-Driven Learning Analytics",
            "XR Immersive Training"
          ]
        })}
      </script>
      {/* WebSite schema for homepage */}
      {path === "/" && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Anugraha Exceed Private Limited",
            "alternateName": ["AEPL", "Anugraha"],
            "url": "https://anugraha.co/"
          })}
        </script>
      )}
      {/* SoftwareApplication schema for product pages */}
      {PRODUCT_SCHEMAS[path] && (
        <script type="application/ld+json">
          {JSON.stringify(PRODUCT_SCHEMAS[path])}
        </script>
      )}
    </Helmet>
  );
};

const PRODUCT_SCHEMAS = {
  "/product/raptor": {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "RAPTOR™",
    "alternateName": "RAPTOR AI Safety & Training Management Platform",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web-based, Cloud, On-Premise, Air-Gapped",
    "url": "https://anugraha.co/product/raptor",
    "description": "RAPTOR is Anugraha's AI/ML-driven Safety & Training Management platform — competency tracking, audit-ready compliance, and real-world scenario simulation.",
    "author": {
      "@type": "Organization",
      "name": "Anugraha Exceed Private Limited",
      "url": "https://anugraha.co/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Anugraha Exceed Private Limited",
      "url": "https://anugraha.co/"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": "2027-12-31"
    }
  },
  "/product/learnon": {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "LearnOn™",
    "alternateName": "LearnOn Adaptive Learning Platform",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web-based, Mobile-responsive",
    "url": "https://anugraha.co/product/learnon",
    "description": "LearnOn personalises training delivery for every learner — part of Anugraha's AI training intelligence suite alongside RAPTOR and GRIFFIN.",
    "author": {
      "@type": "Organization",
      "name": "Anugraha Exceed Private Limited",
      "url": "https://anugraha.co/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Anugraha Exceed Private Limited",
      "url": "https://anugraha.co/"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": "2027-12-31"
    }
  },
  "/product/aurora": {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Aurora™",
    "alternateName": "Aurora AI-Powered Training Analytics",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web-based, Cloud Analytics",
    "url": "https://anugraha.co/product/aurora",
    "description": "Aurora brings AI-driven analytics and insight to competency-based training programmes — part of Anugraha Exceed's training intelligence platform.",
    "author": {
      "@type": "Organization",
      "name": "Anugraha Exceed Private Limited",
      "url": "https://anugraha.co/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Anugraha Exceed Private Limited",
      "url": "https://anugraha.co/"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": "2027-12-31"
    }
  },
  "/product/griffin": {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "GRIFFIN™ XR",
    "alternateName": "GRIFFIN XR Biometric Training Platform",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "VR, AR, Mixed Reality, Web-based",
    "url": "https://anugraha.co/product/griffin",
    "description": "GRIFFIN combines AR/MR/VR with biometric analysis to extend safe, affordable training practice — Anugraha's answer to simulator capacity and cost constraints.",
    "author": {
      "@type": "Organization",
      "name": "Anugraha Exceed Private Limited",
      "url": "https://anugraha.co/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Anugraha Exceed Private Limited",
      "url": "https://anugraha.co/"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": "2027-12-31"
    }
  },
  "/product/courseai": {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CourseAI™",
    "alternateName": "CourseAI Courseware Authoring & Conversion",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web-based, Device-agnostic",
    "url": "https://anugraha.co/product/courseai",
    "description": "CourseAI converts legacy training content into xAPI-compliant, device-agnostic courseware using AI — part of Anugraha Exceed's training intelligence platform.",
    "author": {
      "@type": "Organization",
      "name": "Anugraha Exceed Private Limited",
      "url": "https://anugraha.co/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Anugraha Exceed Private Limited",
      "url": "https://anugraha.co/"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": "2027-12-31"
    }
  }
};

function setMetaTag(attrValue, content, attrName = "name") {
  if (!content) return;
  const elements = document.querySelectorAll(`meta[${attrName}="${attrValue}"]`);
  if (elements.length > 0) {
    elements.forEach((el, index) => {
      if (index === 0) {
        el.setAttribute("content", content);
      } else {
        el.remove();
      }
    });
  } else {
    const element = document.createElement("meta");
    element.setAttribute(attrName, attrValue);
    element.setAttribute("content", content);
    document.head.appendChild(element);
  }
}

export default SEOHead;
